import { useEffect, useState } from "react";

import Svg, { SvgEnum } from "../../../../ui library/components/svgs/standard/svg";
import HNSColors from "../../../../ui library/HNSColors";
import LoadingIndicator from "../../../Common/LoadingIndicator/LoadingIndicator";
import useMedicalBreakdownSection from "../../hooks/useMedicalBreakdownSection";
import { SETTLEMENT_STYLES } from "../../style/styles";

import MedicalBreakdownAddButton from "./MedicalBreakdownAddButton";
import MedicalBreakdownSummary from "./MedicalBreakdownSummary";
import MedicalBreakdownTable from "./MedicalBreakdownTable";
import AddCustomMedicalItemModal from "./modal/AddCustomMedicalItemModal";

export default function MedicalBreakdownSection({ targetHeight = 0 }) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isCompactLayout, setIsCompactLayout] = useState(false);

  const {
    addOpen,
    caseDetail,
    data,
    summary,
    isLoading,
    isSummaryLoading,
    statusOptions,
    canViewMedicalBreakdown,
    canEditMedicalBreakdown,
    handleCloseAddModal,
    handleOpenAddModal,
    handleRefresh,
  } = useMedicalBreakdownSection();
  const hasSettlementAmount =
    caseDetail?.settlement_amount ??
    caseDetail?.settlementAmount ??
    caseDetail?.actual_total_settlement_amount ??
    caseDetail?.actualTotalSettlementAmount;

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 1279px)");

    const updateCompactLayout = (event) => {
      setIsCompactLayout(event.matches);
    };

    updateCompactLayout(mediaQuery);
    mediaQuery.addEventListener("change", updateCompactLayout);

    return () => {
      mediaQuery.removeEventListener("change", updateCompactLayout);
    };
  }, []);

  const effectiveIsExpanded = isCompactLayout || isExpanded;

  if (!canViewMedicalBreakdown) {
    return null;
  }

  return (
    <section
      className="flex min-h-0 flex-col justify-end overflow-hidden rounded-[12px] p-4 shadow-sm"
      style={{
        ...SETTLEMENT_STYLES.sectionCard,
        boxSizing: "border-box",
        ...(targetHeight > 0
          ? effectiveIsExpanded
            ? { minHeight: `${targetHeight}px` }
            : { height: `${targetHeight}px` }
          : {}),
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Svg
              name={SvgEnum.HisClipboardList}
              color={SETTLEMENT_STYLES.sectionIconColor}
              sx={SETTLEMENT_STYLES.sectionIcon}
            />
            <h3 className="text-lg font-semibold" style={SETTLEMENT_STYLES.sectionTitle}>
              Medical Breakdown
            </h3>
          </div>
          <div className="flex items-center gap-3">
            {!isCompactLayout ? (
              <div
                className="inline-flex overflow-hidden rounded-full border"
                style={{
                  borderColor: HNSColors.neutrals[700],
                  backgroundColor: HNSColors.neutrals[900],
                }}
              >
                <button
                  id="medical-breakdown-expand-button"
                  type="button"
                  aria-pressed={effectiveIsExpanded}
                  className="px-4 py-2 text-sm font-semibold transition-colors"
                  style={{
                    backgroundColor: effectiveIsExpanded ? HNSColors.primary[500] : "transparent",
                    color: effectiveIsExpanded ? HNSColors.neutrals[900] : HNSColors.neutrals[200],
                  }}
                  onClick={() => setIsExpanded(true)}
                >
                  Expand
                </button>
                <button
                  id="medical-breakdown-shrink-button"
                  type="button"
                  aria-pressed={!effectiveIsExpanded}
                  className="px-4 py-2 text-sm font-semibold transition-colors"
                  style={{
                    backgroundColor: !effectiveIsExpanded ? HNSColors.primary[500] : "transparent",
                    color: !effectiveIsExpanded ? HNSColors.neutrals[900] : HNSColors.neutrals[200],
                  }}
                  onClick={() => setIsExpanded(false)}
                >
                  Shrink
                </button>
              </div>
            ) : null}
            {canEditMedicalBreakdown ? (
              <MedicalBreakdownAddButton onClick={handleOpenAddModal} />
            ) : null}
          </div>
        </div>

        <MedicalBreakdownTable
          data={data}
          isLoading={isLoading}
          isExpanded={effectiveIsExpanded}
          statusOptions={statusOptions}
          canEdit={canEditMedicalBreakdown}
          onSaveSuccess={handleRefresh}
        />
      </div>
      {hasSettlementAmount ? (
        <div className="relative mt-4">
          {isSummaryLoading ? (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-[12px]"
              style={SETTLEMENT_STYLES.loadingOverlay}
            >
              <LoadingIndicator defaultPadding="" />
            </div>
          ) : null}
          <MedicalBreakdownSummary summary={summary} />
        </div>
      ) : null}

      {canEditMedicalBreakdown ? (
        <AddCustomMedicalItemModal
          openModal={addOpen}
          handleCloseModal={() => handleCloseAddModal(false)}
          caseId={caseDetail?.pk}
          onSaveSuccess={handleRefresh}
        />
      ) : null}
    </section>
  );
}
